import React from 'react';
import { Box, makeStyles, TextField, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';

import { Cart } from '../../components/Cart/Cart';
import { Layout } from '../../components/Layout';
import UploadedPhotos from '../../components/photos/UploadedPhotos';
import { ScrollableContent } from '../../components/ScrollableContent';
import { StepProgressBar } from '../../components/StepProgressBar';
import { useChangeStep } from '../../hooks/useChangeStep';
import { useOrgData } from '../../hooks/useOrgData';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { getItemFromCart } from '../../state/utils';

import { TroubleShootingInput } from './TroubleShootingInput';

const TEXTAREA_MAX_LENGTH = 2000;

const useStyles = makeStyles(() => ({
  charCounter: {
    marginLeft: 'auto',
  },
}));

export const TroubleShootingMergeStep = () => {
  const { charCounter } = useStyles();
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { service, cart, pricingForm, token } = state;
  const { changeStep } = useChangeStep();

  const { isUnscheduled } = useOrgData({ token });

  const selectedItem = service || pricingForm;

  const cartItem = cart.find((item) => getItemFromCart(item).uuid === selectedItem?.uuid);

  if (!selectedItem) {
    return null;
  }

  const { question1, question2 } = selectedItem ?? {};

  const questionOneOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(actions.changeCartItemQuestionOne(selectedItem.uuid, event.target.value));
  };

  const questionTwoOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(actions.changeCartItemQuestionTwo(selectedItem.uuid, event.target.value));
  };

  const descriptionOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(actions.changeCartItemDescription(selectedItem.uuid, event.target.value));
  };

  const handleNext = () => {
    changeStep('contact-details');
  };
  const handleBookMoreServicesClick = () => {
    dispatch(actions.resetFlow());
  };

  const isNextButtonDisabled = cart.some((cItem) => cItem.photos.some((photo) => photo.isLoading));

  return (
    <Layout
      ContentComponent={
        <ScrollableContent>
          <Box mb={2}>
            {question1 && (
              <TroubleShootingInput
                onChange={questionOneOnChange}
                question={question1}
                questionKey="question1"
                dataTestId="question-one"
                value={cartItem?.question_1}
              />
            )}
            {question2 && (
              <TroubleShootingInput
                onChange={questionTwoOnChange}
                question={question2}
                questionKey="question2"
                dataTestId="question-two"
                value={cartItem?.question_2}
              />
            )}
          </Box>
          {!question1 && !question2 && (
            <>
              <Typography gutterBottom variant="h5">
                Additional details (optional)
              </Typography>
              <Box my={2}>
                <TextField
                  value={cartItem?.description}
                  multiline
                  placeholder="Write a short description and include specific details needed for the appointment."
                  data-testid="online-booking-description"
                  helperText={`${state.description.length || cartItem?.description.length || 0}/${TEXTAREA_MAX_LENGTH}`}
                  FormHelperTextProps={{
                    className: charCounter,
                  }}
                  error={(cartItem?.description.length || state.description.length) >= TEXTAREA_MAX_LENGTH}
                  inputProps={{
                    maxLength: TEXTAREA_MAX_LENGTH,
                  }}
                  rows={8}
                  fullWidth
                  onChange={descriptionOnChange}
                />
              </Box>
            </>
          )}
          <UploadedPhotos cartItemUUID={selectedItem?.uuid} />
        </ScrollableContent>
      }
      Buttons={
        <>
          <Button
            version="v2"
            onClick={handleBookMoreServicesClick}
            size="medium"
            fullWidth
            variant="outlined"
            color="secondary"
          >
            Add more services
          </Button>
          <Button
            version="v2"
            size="medium"
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleNext}
            disabled={isNextButtonDisabled}
          >
            Book service
          </Button>
        </>
      }
      Header={
        <>
          <StepProgressBar currentStep={isUnscheduled ? 3 : 2} stepCount={5} />
          <Cart />
        </>
      }
    />
  );
};
